import * as React from 'react';
import { History } from 'history';
import * as UrlParams from 'ts/constants/UrlParams';
import { USERNAME } from 'ts/constants/UrlParams';
import { errorForm, ITaskData, navigation, task } from '@origin-digital/reporting-client';
import { getUsernameError, isAllDigits } from './UsernameValidator';
import {
  sendOneTimeCodeRequest,
  OTCResponse,
} from '../../../services/registration/RegistrationService';
import PageConfig from '../../../../services/userflow/PageConfig';
import { EnterUsernameFormView } from './sub-components/EnterUsernameFormView';
import * as Window from 'ts/window/Window';
import { LiveChat } from 'ts/components/LiveChat';
import { AppContext } from '../../../../contexts/app/AppContext';
import { EmptyEmailBanner, EMPTY_EMAIL_BANNER_TEXT } from './sub-components/EmptyEmailBanner';
import { RetryUsernameBanner } from './sub-components/RetryUsernameBanner';

interface EnterUsernameFormProps {
  history: History;
  location: Location;
  nextAction: (urlParams: string) => void;
  navigateToErrorPage: () => void;
  customErrorAction: (username: string) => void;
  config: PageConfig;
  infoText: string;
}

interface EnterUsernamePageState {
  username: string;
  usernameFieldTouched: boolean;
  spinner: boolean;
  inlineErrorMessage: string;
  showEmptyEmailBanner: boolean;
  failedUsernameAttempts: number;
  showFailedAttemptsAlert: boolean;
}

class EnterUsernameForm extends React.PureComponent<
  EnterUsernameFormProps,
  EnterUsernamePageState
> {
  public state = {
    username: '',
    usernameFieldTouched: false,
    spinner: false,
    inlineErrorMessage: '',
    showEmptyEmailBanner: false,
    failedUsernameAttempts: 0,
    showFailedAttemptsAlert: false,
  };

  public componentDidMount() {
    const usernameHashParam = new URLSearchParams(this.props.location.search).get(USERNAME);
    const usernameUrlParam = new URLSearchParams(Window.getWindow().location.search).get(USERNAME);
    if (usernameUrlParam) {
      this.setState({ username: usernameUrlParam });
    } else if (usernameHashParam) {
      // Not sure why but the param comes in the URL querystring for native, but hash params for web.
      this.setState({ username: usernameHashParam });
    }
    navigation('screen', {
      currentUri: '',
      friendlyUri: `originenergy:my-account:auth:${this.props.config.analytics.pageName}`,
    });
  }

  public render() {
    return (
      <AppContext.Consumer>
        {({ setShowLiveChatWidget }) => (
          <>
            <EmptyEmailBanner
              visible={this.state.showEmptyEmailBanner}
              onCloseClicked={() => this.setState({ showEmptyEmailBanner: false })}
            />
            <RetryUsernameBanner
              isVisible={this.state.showFailedAttemptsAlert}
              usernameSubmitted={this.state.username}
            />
            <EnterUsernameFormView
              username={this.state.username}
              onUsernameBlur={this.usernameBlurred}
              onUsernameChange={this.usernameChanged}
              onSubmit={(event) => this.submit(event, setShowLiveChatWidget)}
              spinner={this.state.spinner}
              fieldNormalizer={removeSpacesNormalizer}
              inlineErrorMessage={this.state.inlineErrorMessage}
              infoText={this.props.infoText}
            />
            <LiveChat />
          </>
        )}
      </AppContext.Consumer>
    );
  }

  private submit = async (e, setShowLiveChatWidget) => {
    e.preventDefault();
    const { username } = this.state;

    const usernameError = getUsernameError(username);
    if (usernameError) {
      this.reportUserNameInvalidFormatError(username);
      setShowLiveChatWidget(true);
      this.setState({
        inlineErrorMessage: usernameError,
      });
      return;
    }

    this.setState({ spinner: true });

    try {
      const response = await sendOneTimeCodeRequest(username);
      if (response.hasBlankEmail) {
        errorForm({
          formName: this.props.config.analytics.taskName,
          errorMessage: EMPTY_EMAIL_BANNER_TEXT,
          errorCode: 'email-does-not-exist',
        });
        this.setState({
          showEmptyEmailBanner: true,
          spinner: false,
        });
      } else if (response.success) {
        this.navigateToOneTimeCode();
      } else {
        this.handleError(response);
      }
    } catch (error) {
      this.props.navigateToErrorPage();
    }
  };

  private handleError(otcResponse: OTCResponse) {
    const { username, failedUsernameAttempts } = this.state;
    if (otcResponse.hasMultipleUsers) {
      this.reportErrorAndNavigateToCustomErrorAction(username); // will navigate to find customer page
      return;
    }
    this.handleFailedAttempts(username, failedUsernameAttempts);
  }

  private handleFailedAttempts = (username: string, failedUsernameAttempts: number) => {
    const currentFailedAttempts = failedUsernameAttempts + 1;
    this.setState({
      failedUsernameAttempts: currentFailedAttempts,
      showFailedAttemptsAlert: true,
      spinner: false,
    });

    this.reportUserNameDoesNotExistError(username);
    if (currentFailedAttempts >= 3) {
      errorForm({
        formName: this.props.config.analytics.taskName,
        errorMessage: `We can't seem to find ${username} in our system.`,
        errorCode: `${this.getUsernameType(username)}-max-attempt`,
      });
      // if failed for 3 times, should navigate to find customer page
      this.props.customErrorAction(username);
    }
  };

  private getUsernameType = (username: string): string =>
    isAllDigits(username) ? 'number' : 'email';

  private reportUserNameDoesNotExistError = (username: string) => {
    this.reportUserNameError(username, `${this.getUsernameType(username)}-does-not-exist`);
  };

  private reportUserNameInvalidFormatError = (username: string) => {
    this.reportUserNameError(username, `${this.getUsernameType(username)}-invalid-format`);
  };

  private reportUserNameError = (username: string, errorCode: string) => {
    errorForm({
      formName: this.props.config.analytics.taskName,
      errorMessage: `Oops, that ${this.getUsernameType(username)} doesn't look right.`,
      errorCode,
    });
  };

  private reportErrorAndNavigateToCustomErrorAction = (username: string) => {
    this.reportUserNameDoesNotExistError(username);
    this.props.customErrorAction(username);
  };

  private usernameBlurred = (e) => {
    const username = e.target.value;
    const usernameFormatError = getUsernameError(username);
    this.setState(
      {
        inlineErrorMessage: usernameFormatError,
      },
      () => this.reportUserNameInvalidFormatError(username),
    );
  };

  private usernameChanged = (e) => {
    const username = e.target.value;
    if (!this.state.usernameFieldTouched) {
      task.base(this.props.config.analytics.taskName as any, { action: 'start' } as ITaskData);
    }

    this.setState({
      username,
      usernameFieldTouched: true,
      showFailedAttemptsAlert: false,
      inlineErrorMessage: getUsernameError(username),
    });
  };

  private navigateToOneTimeCode = () => {
    const urlParams = new URLSearchParams({
      [UrlParams.EMAIL_OR_PHONE]: this.state.username,
    } as any).toString();
    this.props.nextAction(urlParams);
  };
}

const removeSpacesNormalizer = (value): string => value.replace(/\s+/g, '');

export { EnterUsernameForm, removeSpacesNormalizer };
